import React, { useState, useEffect, useMemo } from 'react'
import { Provider, useTable } from 'tinybase/ui-react'
import { SYNC_STATUS } from '../utils/subject_store'

const ProjectSyncStatusContent = ({ projectId, subjectStore, onSyncAll }) => {
  const [loading, setLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)

  // Use TinyBase hooks to trigger updates when tables change
  const subjectsTable = useTable('subjects')

  // Calculate sync stats for this specific project
  const syncStats = useMemo(() => {
    try {
      if (!subjectsTable) {
        return { queueCount: 0 }
      }

      // Filter subjects for this specific project
      const projectSubjects = Object.values(subjectsTable).filter(subject =>
        subject.projectId === projectId
      )

      // Count queued subjects (including those without sync status set yet)
      const queuedSubjects = projectSubjects.filter(subject => {
        return !subject.syncStatus ||
               subject.syncStatus === SYNC_STATUS.PENDING ||
               subject.syncStatus === SYNC_STATUS.IN_PROGRESS
      })

      return { queueCount: queuedSubjects.length }
    } catch (error) {
      console.error('Error calculating project sync stats:', error)
      return { queueCount: 0 }
    }
  }, [subjectsTable, projectId])

  // Wait for store to be ready on mount
  useEffect(() => {
    const initializeStore = async () => {
      try {
        setLoading(true)
        await subjectStore.waitForReady()
      } catch (error) {
        console.error('Error initializing store:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeStore()
  }, [subjectStore])

  const handleSyncAll = async (e) => {
    e.preventDefault() // Prevent link navigation
    e.stopPropagation()

    if (isSyncing) return

    try {
      setIsSyncing(true)
      await onSyncAll(projectId)
    } catch (error) {
      console.error('Error syncing all subjects:', error)
    } finally {
      setIsSyncing(false)
    }
  }

  if (loading) {
    return null // Don't show anything while loading
  }

  return (
    <>
      {/* Queue count badge */}
      {syncStats.queueCount > 0 && (
        <div className="badge badge-warning mt-1 text-base-content">
          <span>{syncStats.queueCount}</span>
          <span>subjects pending</span>
        </div>
      )}

      {/* Sync All button */}
      {syncStats.queueCount > 0 && (
        <button
          onClick={handleSyncAll}
          disabled={isSyncing}
          className="btn btn-primary mt-2"
        >
          {isSyncing ? (
            <>
              <span className="loading loading-spinner loading-xs mr-1"></span>
              Syncing...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sync All
            </>
          )}
        </button>
      )}
    </>
  )
}

// Wrapper component with TinyBase Provider
const ProjectSyncStatus = (props) => {
  const { subjectStore } = props
  return (
    <Provider store={subjectStore.getStore()}>
      <ProjectSyncStatusContent {...props} />
    </Provider>
  )
}

export default ProjectSyncStatus